/**
 * SubmissionsService — API client for `/api/v1/submissions/*`.
 *
 * Mirrors `core/backend/api/submissions.py`. Auth-token handling
 * matches the project's other service classes (Bearer from
 * localStorage). All errors raise an `ApiError` with `kind`/`status`
 * so callers can branch on intent (network vs auth vs validation vs
 * server).
 */

import {
  ApiErrorKind,
  DriverName,
  ImportDeletionSummary,
  ImportJob,
  ImportJobList,
  ImportPreview,
  SubmissionDetail,
  SubmissionList,
} from '../types/submission';
import { readErrorBody } from './apiErrorBody';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const ROOT = '/api/v1/submissions';

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number;
  readonly detail: unknown;
  readonly issues: string[];
  /**
   * The backend's `error_code` / `error_params` (ADR 0005), when the body
   * carried them (TF-772).
   *
   * Optional, because a network failure or a non-JSON body carries neither.
   * They exist so a consumer can hand the code on to an `AppError` instead of
   * the parser discarding it — every parser already reads the body, so the
   * alternative was to parse it a second time somewhere else.
   *
   * Every parser of the family fills them since TF-772 PR 7, through the one
   * shared reader in `apiErrorBody.ts`; the consumers hand the error to
   * `appErrorFromApiError()` instead of rendering `message`, which is
   * log-only from then on.
   */
  readonly errorCode?: string;
  readonly errorParams?: unknown;

  constructor(params: {
    kind: ApiErrorKind;
    status: number;
    message: string;
    detail?: unknown;
    issues?: string[];
    errorCode?: string;
    errorParams?: unknown;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.kind = params.kind;
    this.status = params.status;
    this.detail = params.detail;
    this.issues = params.issues ?? [];
    this.errorCode = params.errorCode;
    this.errorParams = params.errorParams;
  }
}

/**
 * Canonical HTTP status → `ApiErrorKind` mapping (TF-626-Review).
 *
 * Previously, practically every service (httpClient, activityService,
 * gradesService, gradingSchemesService, gradeExportService,
 * moodleFeedbackPushService) maintained its own, slightly divergent copy
 * of this function — among other things, none of them knew about
 * `409 → 'conflict'`, even though `gradingSchemesService`/
 * `gradeExportService` already threw exactly that at runtime. This file
 * is the natural shared place for it: `ApiError` itself is already
 * defined here and imported by the services that still throw it (see
 * `import { ApiError } from './submissionsService'` there).
 *
 * Exported so other services import it instead of keeping their own
 * copy — see httpClient.ts, activityService.ts, gradingSchemesService.ts,
 * gradeExportService.ts, moodleFeedbackPushService.ts.
 *
 * `gradesService.ts` dropped off that list in TF-772: it throws `AppError`
 * with a code per operation now, and imports this function only for the
 * tier-quota 402 it hands to `QuotaBanner` as an `ApiError` (TF-970). The
 * paragraph above keeps naming it, because the history of the divergent
 * copies is what explains why this function exists at all.
 */
export function statusToKind(status: number): ApiErrorKind {
  if (status === 401) return 'auth';
  if (status === 403) return 'permission';
  // 402 falls semantically under "permission" — the user simply lacks
  // *paid* access. QuotaBanner reads `status === 402` directly; this
  // label only serves telemetry/grouping (carried over from httpClient.ts).
  if (status === 402) return 'permission';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 413) return 'too_large';
  if (status === 400 || status === 412 || status === 422) return 'validation';
  if (status >= 500) return 'server';
  return 'unknown';
}

/**
 * Throw an `ApiError` for a failed response. Shared with `httpClient`, which
 * re-exports it — the two modules used to carry identical copies, and only
 * one of them learned to keep `error_code` (TF-772 PR 7).
 *
 * `source` only prefixes the console warning `readErrorBody` logs for a
 * non-JSON body, so `httpClient`'s eight consumers (orgUnits, studentClasses,
 * students, audit, roles, moodleConnections, both ops services) don't show up
 * in the log as "SubmissionsService" — see `httpClient.ts`.
 */
export async function ensureOk(
  response: Response,
  source = 'SubmissionsService',
): Promise<Response> {
  if (response.ok) return response;
  const { message, detail, issues, errorCode, errorParams } = await readErrorBody(
    response,
    source,
  );
  throw new ApiError({
    kind: statusToKind(response.status),
    status: response.status,
    message,
    detail,
    issues,
    errorCode,
    errorParams,
  });
}

function authHeaders(extra: HeadersInit = {}): HeadersInit {
  const token = localStorage.getItem('examcraft_access_token');
  return {
    ...extra,
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

export async function safeFetch(
  input: RequestInfo,
  init?: RequestInit,
): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (err) {
    // Network failures (offline, CORS, DNS) surface here.
    if ((err as { name?: string })?.name === 'AbortError') {
      throw new ApiError({
        kind: 'network',
        status: 0,
        message: 'Anfrage abgebrochen',
      });
    }
    throw new ApiError({
      kind: 'network',
      status: 0,
      message:
        err instanceof Error
          ? `Netzwerkfehler: ${err.message}`
          : 'Netzwerkfehler',
    });
  }
}

export class SubmissionsService {
  static async preview(params: {
    examId: number;
    file: File;
    driverName?: DriverName;
    signal?: AbortSignal;
  }): Promise<ImportPreview> {
    const formData = new FormData();
    formData.append('exam_id', String(params.examId));
    formData.append('driver_name', params.driverName ?? 'moodle_json');
    formData.append('file', params.file);

    const response = await safeFetch(`${API_BASE_URL}${ROOT}/import/preview`, {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
      signal: params.signal,
    });
    await ensureOk(response);
    return (await response.json()) as ImportPreview;
  }

  static async commit(params: {
    examId: number;
    file: File;
    driverName?: DriverName;
    signal?: AbortSignal;
  }): Promise<ImportJob> {
    const formData = new FormData();
    formData.append('exam_id', String(params.examId));
    formData.append('driver_name', params.driverName ?? 'moodle_json');
    formData.append('file', params.file);

    const response = await safeFetch(`${API_BASE_URL}${ROOT}/import/commit`, {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
      signal: params.signal,
    });
    await ensureOk(response);
    return (await response.json()) as ImportJob;
  }

  static async getImportJob(jobId: number): Promise<ImportJob> {
    const response = await safeFetch(
      `${API_BASE_URL}${ROOT}/import-jobs/${jobId}`,
      { method: 'GET', headers: authHeaders() },
    );
    await ensureOk(response);
    return (await response.json()) as ImportJob;
  }

  /**
   * TF-428: list recent import jobs for an exam (newest first) so the
   * Auswertungen view can surface running/finished imports with live
   * progress — the import no longer needs a modal-spinner held open.
   */
  static async listImportJobs(
    examId: number,
    limit = 10,
  ): Promise<ImportJobList> {
    const url = `${API_BASE_URL}${ROOT}/import-jobs?exam_id=${examId}&limit=${limit}`;
    const response = await safeFetch(url, {
      method: 'GET',
      headers: authHeaders(),
    });
    await ensureOk(response);
    return (await response.json()) as ImportJobList;
  }

  /**
   * TF-421: preview how much a result-import delete would remove
   * (affected students/attempts), to populate the confirmation dialog.
   */
  static async getImportSummary(
    examId: number,
  ): Promise<ImportDeletionSummary> {
    const url = `${API_BASE_URL}${ROOT}/import/summary?exam_id=${examId}`;
    const response = await safeFetch(url, {
      method: 'GET',
      headers: authHeaders(),
    });
    await ensureOk(response);
    return (await response.json()) as ImportDeletionSummary;
  }

  /**
   * TF-421: delete all imported results of an exam (across every source)
   * so the operator can re-import cleanly. Requires `submissions:delete`.
   * Returns the counts that were removed.
   */
  static async deleteImport(
    examId: number,
  ): Promise<ImportDeletionSummary> {
    const url = `${API_BASE_URL}${ROOT}/import?exam_id=${examId}`;
    const response = await safeFetch(url, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    await ensureOk(response);
    return (await response.json()) as ImportDeletionSummary;
  }

  static async listForExam(examId: number): Promise<SubmissionList> {
    const url = `${API_BASE_URL}${ROOT}?exam_id=${examId}`;
    const response = await safeFetch(url, {
      method: 'GET',
      headers: authHeaders(),
    });
    await ensureOk(response);
    return (await response.json()) as SubmissionList;
  }

  static async getDetail(submissionId: number): Promise<SubmissionDetail> {
    const response = await safeFetch(
      `${API_BASE_URL}${ROOT}/${submissionId}`,
      { method: 'GET', headers: authHeaders() },
    );
    await ensureOk(response);
    return (await response.json()) as SubmissionDetail;
  }

  /**
   * TF-336: Moodle API import (Pro+ only). Backend throws 402 when the
   * tier doesn't unlock the ``moodle_api`` driver — the caller
   * distinguishes that via ``ApiError.status``.
   */
  static async apiPreview(params: {
    examId: number;
    quizId: number;
    signal?: AbortSignal;
  }): Promise<ImportPreview> {
    const response = await safeFetch(
      `${API_BASE_URL}${ROOT}/import/api-preview`,
      {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          exam_id: params.examId,
          quiz_id: params.quizId,
        }),
        signal: params.signal,
      },
    );
    await ensureOk(response);
    return (await response.json()) as ImportPreview;
  }

  static async apiCommit(params: {
    examId: number;
    quizId: number;
    signal?: AbortSignal;
  }): Promise<ImportJob> {
    const response = await safeFetch(
      `${API_BASE_URL}${ROOT}/import/api-commit`,
      {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          exam_id: params.examId,
          quiz_id: params.quizId,
        }),
        signal: params.signal,
      },
    );
    await ensureOk(response);
    return (await response.json()) as ImportJob;
  }
}
